// @flow
/* eslint react/jsx-no-bind:0 */

import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import * as StringUtil from 'app/common/lib/StringUtil';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import Experience from 'app/common/react/components/experiences/Experience/Experience';
import ExperimentsClient from 'app/common/lib/ExperimentsClient';
import FacebookConnectButton from 'app/common/react/components/growth/unauth/signup/FacebookConnectButton/FacebookConnectButton';
import FacebookEmailCollection from 'app/common/react/components/growth/unauth/signup/FacebookEmailCollection/FacebookEmailCollection';
import FullPageSignupTitle from 'app/common/react/components/growth/unauth/signup/FullPageSignup/FullPageSignupTitle';
import i18n from 'app/common/lib/i18n';
import loadable from '@loadable/component';
import LoadingMask from 'app/common/react/components/growth/unauth/signup/LoadingMask/LoadingMask';
import Login from 'app/common/react/components/growth/unauth/signup/Login/Login';
import LoginMfa from 'app/common/react/components/growth/unauth/signup/LoginMfa/LoginMfa';
import LoginWelcomeLoading from 'app/common/react/components/growth/unauth/signup/LoginWelcomeLoading/LoginWelcomeLoading';
import logo from 'app/common/react/components/growth/unauth/lib/images/logo';
import nameFromEmail from 'app/packages/infer-name-from-email';
import PropTypes from 'prop-types';
import Separator from 'app/common/react/components/growth/unauth/signup/Separator/Separator';
import setEmailTaken from 'app/www-unauth/redux/actions/loggedOutUserActions';
import Signup from 'app/common/react/components/growth/unauth/signup/Signup/Signup';
import SignupLoginToggleButton from 'app/common/react/components/growth/unauth/signup/SignupLoginToggleButton/SignupLoginToggleButton';
import SignupWelcomeLoading from 'app/common/react/components/growth/unauth/signup/SignupWelcomeLoading/SignupWelcomeLoading';
import SocialConnectAgeCollection from 'app/common/react/components/growth/unauth/signup/SocialConnectAgeCollection/SocialConnectAgeCollection';
import templateConst from 'app/common/lib/templateConst';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { CloseupPin } from 'app/packages/pinterest-flow-types/pin';
import type { Dispatch } from 'redux';
import type { FullPageSignupToggle } from 'app/common/react/components/growth/unauth/signup/FullPageSignup/FullPageSignupToggleType';
import type { GenericAuthenticationCredsType } from 'app/packages/pinterest-authentication/registration';
import type { InviterFlowType } from 'app/common/react/components/growth/unauth/SocialAccountUpsell/constants';
import type { State as ReduxState } from 'app/www-unauth/redux/reducers/types';
import urlUtil from 'app/common/lib/urlUtil';
import withSeoExperiments from 'app/common/lib/react/withSeoExperiments';
import withUnauthSeoExperiments from 'app/common/lib/react/withUnauthSeoExperiments';
import { compose } from 'redux';
import { FullPageSignupToggleType } from 'app/common/react/components/growth/unauth/signup/FullPageSignup/FullPageSignupToggleType';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { track } from 'app/common/lib/react/analytics';
import { trackMagicLink } from 'app/common/lib/MagicLinkUtils';
import EducationVideoExperimentUtils, {
  EDUCATION_VIDEO_IN_MODAL_HEIGHT,
  EDUCATION_VIDEO_IN_MODAL_MARGIN,
  EDUCATION_VIDEO_IN_MODAL_SMALL_HEIGHT,
  EDUCATION_VIDEO_IN_MODAL_MEDIUM_HEIGHT,
  EDUCATION_VIDEO_IN_MODAL_WIDTH,
} from 'app/common/react/components/growth/unauth/PinLandingCloseup/EducationVideoExperimentUtils';
import trackRegisterAction, {
  trackRegisterActionWithAuxData,
} from 'app/common/lib/util/trackRegisterAction';
import type { LoginCreds, UnauthUserInfo } from 'app/packages/pinterest-flow-types/authentication';
import {
  handleLoginSecondAccountFailure,
  trackMultipleAccounts,
} from 'app/common/lib/MultipleAccountsUtils';
import {
  ModalTypes,
  SignupTypes,
  SignupSource,
  type SignupSourceType,
} from 'app/common/react/components/growth/unauth/signup/constants';
import {
  RequestContextConsumer,
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Avatar, Box, Button as GestaltButton, Divider, Link, Text, Touchable } from 'gestalt';
import { matchPath, type Location } from 'react-router-dom';

const UnauthVideo = loadable(() =>
  import(/* webpackChunkName: "UnauthVideo" */ 'app/common/react/components/growth/unauth/Video/UnauthVideo'),
);

const cannonicalBusinessSignupURL = templateConst.settings.PARTNER_SITE_SIGNUP;

const styles = {
  signup: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    position: 'relative',
    textAlign: 'center',
    width: '404px',
    margin: 'auto',
    minHeight: '450px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
  },
  signupMobile: {
    backgroundColor: '#fff',
    position: 'absolute',
    left: '0',
    right: '0',
    top: '0',
    minHeight: '100vh',
    textAlign: 'center',
  },
  signupModal: {
    textAlign: 'center',
  },
  signupForm: {
    minHeight: '400px',
    padding: '20px 10px',
  },
  signupInviteForm: {
    minHeight: '400px',
    padding: '30px 10px 20px',
  },
  signupTitleContainer: {
    margin: '0 auto 25px',
    width: '270px',
  },
  signupTitleMfaContainer: {
    padding: '0 10px',
  },
  signupTitle: {
    color: '#333',
    fontSize: '32px',
    fontWeight: 'bold',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    letterSpacing: '-1.2px',
  },
  haveAccountText: {
    display: 'inline-block',
    marginRight: '4px',
  },
  logoContainer: {
    display: 'block',
    height: '45px',
    margin: '5px auto',
    width: '45px',
  },
  pinterestLogo: {
    textAlign: 'center',
    paddingTop: '20px',
    display: 'inline-block',
  },
  inputFields: {
    backgroundColor: '#f0f0f0',
    border: '0px',
    outline: 'none',
  },
  separator: {
    borderBottom: '#efefef 1px solid',
    margin: '20px 0px 15px -68px',
    width: '404px',
  },
  signupLoginToggle: {
    color: '#333',
    fontSize: '14px',
    fontWeight: 'normal',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  profilePic: {
    margin: '0 auto 16px',
    width: '108px',
  },
  personalizedModalContainer: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '22px 0',
    position: 'relative',
    textAlign: 'center',
    width: '400px',
    margin: 'auto',
    boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
  },
  closeButton: {
    backgroundColor: 'transparent',
    backgroundRepeat: 'no-repeat',
    border: 'none',
    outline: 'none',
    overflow: 'hidden',
    padding: '10px',
    position: 'absolute',
    right: '10px',
    top: '10px',
  },
  notNowButton: {
    border: 'none',
    color: '#717171',
    fontWeight: 'bold',
    textDecoration: 'none',
    backgroundColor: '#eee',
    height: '36px',
    borderRadius: '5px',
    fontSize: '16px',
    width: '268px',
    marginTop: '10px',
  },
  onClickEmailConfirmationButton: {
    width: '270px',
    marginTop: '40px',
  },
  signupSubtitles: {
    textAlign: 'center',
    color: '#333',
    fontSize: '16px',
    fontWeight: 'normal',
    margin: '-15px 0 20px',
  },
  businessHeader: {
    float: 'left',
    color: '#333',
    fontSize: '14px',
    fontWeight: 'normal',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  businessLink: {
    cursor: 'pointer',
    marginLeft: '5px',
    float: 'left',
  },
  businessContainer: {
    margin: '10px auto 5px',
    display: 'flex',
    alignItems: 'baseline',
    width: 'fit-content',
  },
  closeButtonStyle: {
    position: 'absolute',
    right: '10px',
    paddingTop: '10px',
    zIndex: '1',
  },
};

type Props = {|
  AMPClientId?: string,
  container: string,
  customLoginTagline?: string,
  customSignupTagline?: string,
  darkSignupTitle?: boolean,
  delayedTitleRender?: boolean,
  disablePersonalizedLoginActivation?: boolean,
  email?: string,
  emailTaken: boolean,
  emailValidationError?: string,
  handleCancelClick?: () => void,
  handleModalTypeSwitch?: () => void,
  handleShowingSignupWelcomeLoading?: (boolean) => void,
  hybridSideBar?: boolean,
  hybridTier?: string,
  inviter?: InviterFlowType,
  isAccountSwitch?: boolean,
  isBusiness?: boolean,
  handleCloseClick?: (event: SyntheticEvent<>) => void,
  hideAccountSwitchValueProp?: boolean,
  location: Location,
  magicLinkGroup?: string,
  modal?: boolean,
  mfaCreds?: LoginCreds,
  nextUrlParam?: string,
  pageType: string,
  passwordValidationError?: string,
  pins: { [key: string]: CloseupPin },
  postSignupUrlParam?: string,
  renderBusinessCreateButton: () => void,
  requestContext: RequestContextType,
  seoExperiments?: ExperimentsClient,
  seoUnauthExperiments: ExperimentsClient,
  signupSource?: SignupSourceType,
  showCancelButton?: boolean,
  showResponseMessageModal?: (isSignup: boolean) => void,
  socialRegistrationCredentials: GenericAuthenticationCredsType,
  toggleType: FullPageSignupToggle,
  type: string,
  userInfo?: UnauthUserInfo,
  viewer: AuthViewer,

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  dispatch: Dispatch<*>,
  eligibleExperiences: {},
  languageLocation: 'loginPage' | 'homePage',
  toggleModalType?: () => void,
  pinCategory?: string,
  setEmailAvailable: () => void,
|};

type State = {|
  clearError: boolean,
  type: string,
  email: string,
  password: string,
  age: ?string,
  gender: string,
  mfaCode: string,
  mfaCreds: ?LoginCreds,
  mfaPhoneNumberEnd: string,
  name: string,
  isAccountSwitch?: boolean,
  isExitingUserInSignup: boolean,
  isPersonalizedModal: ?boolean,
  isLoading: boolean,
  socialRegistrationCredentials: GenericAuthenticationCredsType,
  step: string,
  showSignupWelcomeLoading: boolean,
  showLoginWelcomeLoading: boolean,
  userInfo?: UnauthUserInfo,
  headerStyle?: string,
  mfaPhoneNumberEnd: string,
  isShowRegisterForm: boolean,
|};

const signupTitleExpNameCategoryMap = {
  diy_crafts: 'web_unauth_closeup_signup_title_diy_crafts',
  food_drink: 'web_unauth_closeup_signup_title_food_drink',
  womens_fashion: 'web_unauth_closeup_signup_title_womens_fashion',
  home_decor: 'web_unauth_closeup_signup_title_home_decor',
  art: 'web_unauth_closeup_signup_title_art',
};

const signupTitleExpType1Locales = ['cs-CZ', 'da-DK', 'es-AR'];

const signupTitleExpType2Locales = [
  'en',
  'es-ES',
  'es-MX',
  'fr-FR',
  'hi-IN',
  'hu-HU',
  'it-IT',
  'pl-PL',
  'pt-BR',
];

class FullPageSignup extends Component<Props, State> {
  static defaultProps = {
    disablePersonalizedLoginActivation: false,
    isAccountSwitch: false,
    signupSource: SignupSource.defaultSource,
    toggleType: FullPageSignupToggleType.DEFAULT_TOGGLE,
    type: SignupTypes.signup,
  };

  constructor(props) {
    super(props);
    const urlParams = urlUtil.getQueryStringParams();

    this.state = {
      clearError: false,
      type: urlParams.type === 'signup' ? SignupTypes.signup : this.props.type,
      email: props.email || '',
      password: '',
      age: undefined,
      gender: '',
      mfaCode: '',
      mfaCreds: props.mfaCreds || null,
      mfaPhoneNumberEnd: '',
      name: '',
      isExitingUserInSignup: false,
      isPersonalizedModal: null,
      isLoading: false,
      socialRegistrationCredentials: this.props.socialRegistrationCredentials,
      step: 'signup',
      showLoginWelcomeLoading: false,
      showSignupWelcomeLoading: false,
      isShowRegisterForm: false,
    };
  }

  componentDidMount() {
    const { AMPClientId, userInfo, viewer } = this.props;

    this.logModalShown(this.state.type);
    if (this.context.logging) {
      if (viewer.isAuth) {
        track(PCONST.thrift.EventType.PLAIN_MODAL_SIGNUP_VIEW, {
          view: this.context.logging.viewType,
          viewParameter: this.context.logging.viewParameter,
          login_state: viewer.isLimitedLogin
            ? PCONST.LoginStates.LIMITED
            : PCONST.LoginStates.DEFAULT,
        });
      } else {
        track(PCONST.thrift.EventType.PLAIN_MODAL_SIGNUP_VIEW, {
          view: this.context.logging.viewType,
          viewParameter: this.context.logging.viewParameter,
        });
      }
    }

    if (AMPClientId) {
      trackRegisterActionWithAuxData('signup_page_from_amp', {
        amp_client_id: AMPClientId,
      });
    }

    if (viewer.isAuth && viewer.isLimitedLogin) {
      const limitedLoginUserInfo = {
        connected_to_facebook: this.isLimitedLoginUserFBConnected(),
        connected_to_google: this.isLimitedLoginUserGplusConnected(),
        email: viewer.email,
        first_name: viewer.firstName,
        has_password: viewer.hasPassword,
        medium_image_url: viewer.imageMediumUrl,
      };
      this.setPersonalizedModal(limitedLoginUserInfo);
    } else {
      this.setPersonalizedModal(userInfo);
    }
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    if (nextState.type !== this.state.type) {
      // Log a modal view if the type differs from the previous modal type
      this.logModalShown(nextState.type);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.userInfo !== prevProps.userInfo) {
      this.setPersonalizedModal(this.props.userInfo);
    }
    if (this.props.emailTaken && this.props.emailTaken !== prevProps.emailTaken) {
      const inferredUser = {
        connected_to_facebook: true,
        connected_to_google: true,
        email: this.state.email,
        first_name: nameFromEmail(this.state.email),
        has_password: true,
        medium_image_url: '',
      };
      this.setPersonalizedModal(inferredUser);
    }

    // TODO: JIRA CON-1634, move the prop change logic to getDerivedStateFromProps and update parent component props accordingly
    if (this.props.type !== prevProps.type) {
      /* eslint-disable react/no-did-update-set-state */
      this.setState({
        type: this.props.type,
        email: this.props.email || '',
        password: '',
        age: undefined,
        gender: '',
        mfaCode: '',
        mfaCreds: this.props.mfaCreds || null,
        mfaPhoneNumberEnd: '',
        name: '',
        socialRegistrationCredentials: this.props.socialRegistrationCredentials,
      });
      /* eslint-enable react/no-did-update-set-state */
    }
  }

  componentWillUnmount() {
    const { isAccountSwitch, container } = this.props;

    if (isAccountSwitch) {
      trackMultipleAccounts('add_account_dismiss', null, container);
      handleLoginSecondAccountFailure(container);
    }
  }

  getSignupStyles() {
    const isModal = this.props.modal;
    const signupStyle = this.getSignupBoxStyle();
    if (isModal) {
      return styles.signupModal;
    } else {
      return signupStyle;
    }
  }

  getNextUrlParam = () => {
    const { nextUrlParam } = this.props;
    if (nextUrlParam) {
      return decodeURIComponent(nextUrlParam);
    }
    return nextUrlParam;
  };

  getNextUrl = (baseUrl) => {
    if (this.getNextUrlParam()) {
      return urlUtil.buildRelativeUrl(baseUrl, { next: this.getNextUrlParam() });
    }

    return baseUrl;
  };

  setModalType(type, e) {
    this.setState({ type });
  }

  setNameFromEmail() {
    this.setState({ name: nameFromEmail(this.state.email) });
  }

  setPersonalizedModal(userInfo) {
    const { emailTaken, signupSource, isAccountSwitch, toggleModalType } = this.props;
    const { isPersonalizedModal, type } = this.state;

    if (isAccountSwitch) {
      return;
    }

    if (userInfo && Object.keys(userInfo).length > 0 && (!isPersonalizedModal || emailTaken)) {
      if (userInfo.medium_image_url.endsWith('user/default_150.png')) {
        userInfo.medium_image_url = '';
      }

      this.setState({
        isPersonalizedModal: true,
        userInfo,
      });

      if (
        signupSource === SignupSource.homePage &&
        type === SignupTypes.signup &&
        toggleModalType
      ) {
        // The parent modal state needs to be toggled to reflect the local modal state
        toggleModalType();
      }
    }
  }

  getSignupBoxStyle = () => {
    const { container } = this.props;

    if (container === 'home_page') {
      return { ...styles.signup, minHeight: 'initial' };
    }

    return styles.signup;
  };

  showAdditionalStep = (stepName) => (network, userData, credentialsAndLoggingData) => {
    this.setState({
      type: stepName,
      socialRegistrationCredentials: {
        type: network,
        userData,
        credentialsAndLoggingData,
      },
    });
  };

  tagline = (type, signupSource, seoUnauthExperiments) => {
    const defaultSource = i18n._(
      'Sign up to see more',
      'sigup copy default includes pin click and scrolling',
    );

    const signupTaglines = {
      defaultSource,
      boardInvite: i18n._('Sign up to accept your board invite', 'Sign up copy board invite'),
      userFollow: i18n._('Sign up to see more of this profile', 'signup copy user follow'),
      userProfilePage: i18n._('Sign up to see more of this profile', 'signup copy profile page'),
      saveButton: i18n._('Sign up to see more', 'signup copy save button'),
      signupButton: i18n._('Sign up to see more', 'signup copy signup button'),
      homePage: i18n._('Welcome to Pinterest', 'Title on desktop signup modal on home page'),
      login: defaultSource,
      loginMfa: defaultSource,
      actionButton: defaultSource,
      boardPage: defaultSource,
      giftWrap: defaultSource,
      invite: defaultSource,
      mwebDefaultSource: defaultSource,
      navigateBoard: defaultSource,
      navigateInterest: defaultSource,
      navigatePin: defaultSource,
      personalizedFlyout: defaultSource,
      unauthVisualSearch: defaultSource,
      unauthBanner: defaultSource,
      unauthHeader: defaultSource,
    };

    const login = i18n._('Log in to see more', 'Title on desktop login modal');

    const loginTaglines = {
      boardInvite: i18n._('Log in to accept your board invite', 'Login copy board invite'),
      invite: i18n._('Log in to invite friends'),
      login,
      saveButton: i18n._('Log in to see more', 'Title on desktop login modal'),
      homePage: i18n._('Welcome to Pinterest', 'Title on desktop signup modal on home page'),
      defaultSource: login,
      loginMfa: login,
      actionButton: login,
      boardPage: login,
      giftWrap: login,
      unauthVisualSearch: login,
      mwebDefaultSource: login,
      navigateBoard: login,
      navigateInterest: login,
      navigatePin: login,
      personalizedFlyout: login,
      signupButton: login,
      unauthBanner: login,
      userFollow: login,
      userProfilePage: login,
      unauthHeader: login,
    };

    const loginMfaTaglines = {
      login: i18n._('Two-Factor Authentication Required'),
    };

    let tagline;
    if (type === SignupTypes.signup || type === SignupTypes.signupOrPersonalizedLogin) {
      tagline = signupTaglines[signupSource];
    } else if (type === SignupTypes.loginMfa) {
      tagline = loginMfaTaglines[SignupSource.login];
    } else {
      tagline = loginTaglines[signupSource];
    }
    return tagline;
  };

  valuePropAsSubtitle = (signupSource: ?SignupSourceType) => {
    if (signupSource === SignupSource.homePage) {
      return i18n._('Find new ideas to try', 'Value prop on desktop signup modal on home page');
    }
    return i18n._('Discovery starts here', 'Pinterest value proposition on desktop login modal');
  };

  valuePropAsTitle = () =>
    i18n._('Access our best ideas with a free account', 'Value prop on desktop signup modal');

  signupTitleControlText = () => {
    const { requestContext } = this.props;
    const { language, country } = requestContext;
    const userLocale = `${language}-${country}`;
    if (signupTitleExpType1Locales.includes(userLocale)) {
      return i18n._('Free, unlimited access to ideas', 'Value prop on desktop signup modal');
    } else if (signupTitleExpType2Locales.includes(userLocale)) {
      return i18n._(
        "Unlimited free access to the world's best ideas",
        'Value prop on desktop signup modal',
      );
    } else {
      return i18n._(
        'Access our best ideas with a free account',
        'Value prop on desktop signup modal',
      );
    }
  };

  signupTitleTextExpGroupMapping = (group: string) => {
    const { pinCategory } = this.props;

    const textGroupMap = {
      diy_crafts: {
        enabled1: i18n._(
          'Find millions of ideas for your next DIY project with a free account',
          'Value prop on desktop signup modal on diy crafts closeup',
        ),
        enabled2: i18n._(
          'On the hunt for a new DIY project? We’ve got you covered',
          'Value prop on desktop signup modal on diy crafts closeup',
        ),
      },
      food_drink: {
        enabled1: i18n._(
          'Find your next food and drink idea with a free account',
          'Value prop on desktop signup modal on food and drink closeup',
        ),
        enabled2: i18n._(
          'Dig into millions of mouthwatering food and drink ideas',
          'Value prop on desktop signup modal on food and drink closeup',
        ),
      },
      womens_fashion: {
        enabled1: i18n._(
          'Still looking for some fashion inspiration?',
          'Value prop on desktop signup modal on women fashion closeup',
        ),
        enabled2: i18n._(
          'A fresh idea is always in style',
          'Value prop on desktop signup modal on women fashion closeup',
        ),
      },
      home_decor: {
        enabled1: i18n._(
          'Get inspired and give your home a new look',
          'Value prop on desktop signup modal on home decor closeup',
        ),
        enabled2: i18n._(
          'The home of the world’s best decor ideas',
          'Value prop on desktop signup modal on home decor closeup',
        ),
      },
      art: {
        enabled1: i18n._(
          'Find your new favorite masterpiece with a free account',
          'Value prop on desktop signup modal on art closeup',
        ),
        enabled2: i18n._(
          'The best, boldest, brightest ideas all in one place',
          'Value prop on desktop signup modal on art closeup',
        ),
      },
    };

    if (
      group.includes('control') ||
      !pinCategory ||
      !textGroupMap[pinCategory] ||
      !textGroupMap[pinCategory][group]
    ) {
      return this.signupTitleControlText();
    }

    return textGroupMap[pinCategory][group];
  };

  switchToSignup = () => {
    this.setModalType(SignupTypes.signup);
  };

  toggleMagicLinkConfirmation = (show: boolean) => {
    this.setState({ type: show ? SignupTypes.magicLinkConfirmation : SignupTypes.login });
  };

  logModalShown(modalType) {
    trackRegisterAction(`unauth.authentication_modal.shown.${this.props.toggleType}.${modalType}`);
  }
  trackScopedAction(action) {
    trackRegisterAction(
      `unauth.authentication_modal.${action}.${this.props.toggleType}.${this.state.type}`,
    );
  }
  clearPassword = () => {
    this.setState({ password: '' });
  };

  handleDataChange(field, e) {
    const data = {};
    data[field] = e.target.value;
    this.setState(data);
  }
  handleLoginWithMfa(creds, data) {
    const newState = {
      mfaCreds: creds,
      mfaPhoneNumberEnd: undefined,
    };
    if (data && data.phone_number_end) {
      newState.mfaPhoneNumberEnd = data.phone_number_end;
    }
    this.setState(newState);
    this.setModalType(SignupTypes.loginMfa);
  }

  handlePreventRegisterFromPersonalizedModal = (loginType: string) => {
    trackRegisterAction(`unauth.personalized_login.prevent_register.${loginType}`);
    this.setState({
      headerStyle: 'default',
      isPersonalizedModal: false,
      isLoading: false,
    });
    this.switchToSignup();
  };

  resetInferredUser = () => {
    const { emailTaken, setEmailAvailable } = this.props;
    if (emailTaken) {
      setEmailAvailable();
      this.setState({
        isPersonalizedModal: false,
        userInfo: undefined,
      });
    }
  };

  handleNotYouClick = (e) => {
    const { viewer } = this.props;
    e.event.preventDefault();

    this.resetInferredUser();

    if (this.state.type === SignupTypes.FBEmailAlreadyExist) {
      this.setState({
        headerStyle: 'default',
        isPersonalizedModal: false,
        password: '',
        type: SignupTypes.login,
      });
    } else {
      this.setState({
        headerStyle: 'default',
        isPersonalizedModal: false,
        password: '',
      });
    }

    trackRegisterAction('unauth.personalized_login.not_you.click');
    if (viewer.isAuth) {
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.NOT_YOU_LINK,
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
      });
    } else {
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.NOT_YOU_LINK,
      });
    }
  };

  changeStep(step) {
    this.setState({ step });
  }
  toggleLoadingIndicator = (on) => {
    this.setState({ isLoading: on });
  };

  handleLoginToggleButtonClick = (e) => {
    const { handleModalTypeSwitch } = this.props;

    this.trackScopedAction('login_toggle_click');

    if (handleModalTypeSwitch) {
      handleModalTypeSwitch();
    }
    this.setModalType(SignupTypes.login, e);
    this.setWelcomeBackMessageForExistingEmail(false);
  };

  handleSignupToggleButtonClick = (e) => {
    const {
      handleModalTypeSwitch,
      seoUnauthExperiments,
      pageType,
      hybridTier,
      requestContext,
      eligibleExperiences,
    } = this.props;
    this.trackScopedAction('signup_toggle_click');

    if (handleModalTypeSwitch) {
      handleModalTypeSwitch();
    }
    this.setModalType(SignupTypes.signup, e);

    const isInEducationVideoInModalGroup =
      EducationVideoExperimentUtils(
        seoUnauthExperiments,
        eligibleExperiences,
        pageType,
        true,
        hybridTier,
        requestContext.isBot,
      ) &&
      (this.state.type === SignupTypes.login || this.state.type === SignupTypes.signup);

    //When first time render the signup modal,
    // the registerForm wont show up
    if (isInEducationVideoInModalGroup) {
      this.setState({ isShowRegisterForm: false });
    }
  };

  handleConfirmationPageButtonClick = (e) => {
    this.setModalType(SignupTypes.login, e);
  };

  handleNotNowClick = (e) => {
    this.setModalType(SignupTypes.signup, e);
    this.toggleLoadingIndicator(false);
  };

  handleSignupToggleFromPersonalized = (e) => {
    const { viewer } = this.props;
    e.event.preventDefault();

    this.resetInferredUser();

    trackRegisterAction('unauth.personalized_login.signup_instead.click');
    this.setState({
      password: '',
    });
    if (viewer.isAuth) {
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.NO_ACCOUNT_LINK,
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
      });
    } else {
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.NO_ACCOUNT_LINK,
      });
    }
    this.handleSignupToggleButtonClick(e);
  };

  handlePostConnectClick = () => {
    trackRegisterAction('unauth.fb_email_reauth.post_connect.click');
  };

  handlePostSuccessConnect = () => {
    trackRegisterAction('unauth.fb_email_reauth.post_success.connect');
  };

  handleShowRegisterForm = () => {
    this.setState({ isShowRegisterForm: true });
  };

  updateEmail = (email?: string) => {
    this.setState({ email, clearError: true });
  };

  isUnidentifiedSignupMethod() {
    const { viewer } = this.props;
    if (viewer.isAuth && viewer.isLimitedLogin) {
      return this.isUnidentifiedLimitedLoginSignupMethod();
    }

    const { userInfo } = this.state;

    return (
      userInfo &&
      !(userInfo.has_password || userInfo.connected_to_facebook || userInfo.connected_to_google)
    );
  }

  isUnidentifiedLimitedLoginSignupMethod() {
    const { viewer } = this.props;
    return (
      viewer.isAuth &&
      viewer.isLimitedLogin &&
      !(
        viewer.hasPassword ||
        this.isLimitedLoginUserGplusConnected() ||
        this.isLimitedLoginUserFBConnected()
      )
    );
  }
  isLimitedLoginUserFBConnected() {
    const { viewer } = this.props;
    return (
      viewer.socialNetwork &&
      viewer.socialNetwork.facebook &&
      viewer.socialNetwork.facebook.connected
    );
  }
  isLimitedLoginUserGplusConnected() {
    const { viewer } = this.props;
    return (
      viewer.socialNetwork && viewer.socialNetwork.gplus && viewer.socialNetwork.gplus.connected
    );
  }
  showLoginWelcomeLoadingFn = (shouldShow: boolean) => {
    this.setState({
      showLoginWelcomeLoading: shouldShow,
    });
  };
  showSignupWelcomeLoadingFn = (shouldShow: boolean) => {
    this.setState({
      showSignupWelcomeLoading: shouldShow,
    });
    if (this.props.handleShowingSignupWelcomeLoading) {
      this.props.handleShowingSignupWelcomeLoading(shouldShow);
    }
  };

  setWelcomeBackMessageForExistingEmail = (shouldSet: boolean) => {
    this.setState({
      isExitingUserInSignup: shouldSet,
    });
  };

  renderModalSideImage(image, modalHeight = 600) {
    const { image_url: imageUrl, image_aspect_ratio: imageRatio } = image || {};
    const imageStyle = {
      height: '100%',
      width: imageRatio * modalHeight,
      maxWidth: 400,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundImage: `url(${imageUrl})`,
    };
    return (
      <div style={{ borderRadius: '8px 0 0 8px', overflow: 'hidden' }}>
        <div style={imageStyle} />
      </div>
    );
  }
  renderModalSideVideo(
    isSignup: boolean = false,
    url: string,
    thumbnail: string,
    duration: number,
  ) {
    const { isShowRegisterForm, type } = this.state;
    let videoHeight = EDUCATION_VIDEO_IN_MODAL_HEIGHT;
    const videoWidth = EDUCATION_VIDEO_IN_MODAL_WIDTH;
    if (type === SignupTypes.login) {
      videoHeight = EDUCATION_VIDEO_IN_MODAL_MEDIUM_HEIGHT;
    } else if (type === SignupTypes.signup) {
      if (isShowRegisterForm) {
        videoHeight = EDUCATION_VIDEO_IN_MODAL_HEIGHT;
      } else {
        videoHeight = EDUCATION_VIDEO_IN_MODAL_SMALL_HEIGHT;
      }
    }
    return (
      <UnauthVideo
        autoplay
        muted
        margin={EDUCATION_VIDEO_IN_MODAL_MARGIN}
        video={{
          mp4: {
            url,
            thumbnail,
            duration,
            width: videoWidth,
            height: videoHeight,
          },
        }}
        view={this.context.logging.viewType}
        viewParameter={this.context.logging.viewParameter}
        componentLogging={PCONST.thrift.ComponentType.UNAUTH_VIDEO}
        modalOpened={false}
        fallback={<div />}
      />
    );
  }
  renderLoadingIndicator() {
    if (!this.state.isLoading) {
      return null;
    }
    return <LoadingMask />;
  }
  renderPersonalizedLogin() {
    const {
      container,
      darkSignupTitle,
      delayedTitleRender,
      hybridTier,
      isBusiness,
      location,
      passwordValidationError,
      pins,
      viewer,
      type,
    } = this.props;
    const { isPersonalizedModal, showLoginWelcomeLoading } = this.state;

    let sideImage;
    const matchCloseupPage = matchPath(location.pathname, { path: '/pin/:id', exact: true });
    if (viewer.isLimitedLogin && !!matchCloseupPage) {
      const pinId = matchCloseupPage.params.id;
      const pinImage = pinId && pins[pinId] && pins[pinId].images && pins[pinId].images['474x'];
      sideImage = pinImage && {
        image_url: pinImage.url,
        image_aspect_ratio: pinImage.width / pinImage.height || 0,
      };
    }
    const notYouText = i18n._(
      'Not you? Log in with a different account',
      'Short message (in small fonts) on the bottom of the login modal that prompts a user to log in to a different account',
    );
    const needAccountText = i18n._(
      'Need an account? Sign up now',
      'Short message (in small fonts) on the bottom of the login modal that prompts a users to create an account',
    );
    const name = this.state.userInfo ? this.state.userInfo.first_name : '';
    const welcomeNameText = interpolateNamedTemplate(
      i18n._(
        'Welcome back, {{name}}!',
        'Short title (large font) on the login modal welcoming back a user who previously logged in to Pinterest',
      ),
      { name },
    );

    const logoContainerStyles = styles.logoContainer;
    const signupTitleContainer = {
      ...styles.signupTitleContainer,
      width: '350px',
      overflow: 'hidden',
      maxHeight: '120px',
    };

    const personalizedLoginModal = (
      <Fragment>
        {this.renderLoadingIndicator()}
        <div style={logoContainerStyles}>{logo(40, true)}</div>
        <div style={signupTitleContainer}>
          {!delayedTitleRender && (
            <FullPageSignupTitle
              container={container}
              darkSignupTitle={darkSignupTitle}
              hybridTier={hybridTier}
              modalType={ModalTypes.personalizedLogin}
              titleText={welcomeNameText}
            />
          )}
        </div>
        <div style={styles.profilePic}>
          <Avatar
            src={this.state.userInfo ? this.state.userInfo.medium_image_url : ''}
            name={this.state.userInfo ? this.state.userInfo.first_name : ''}
          />
        </div>
        <Box marginBottom={3}>
          <Text size="md" align="center">
            {this.state.userInfo ? this.state.userInfo.email : null}
          </Text>
        </Box>
        <RequestContextConsumer>
          {(rq) => (
            <Login
              requestContext={rq}
              container={this.props.container}
              disablePostSignupNextUrl
              email={this.state.userInfo ? this.state.userInfo.email : null}
              loadingIndicatorCallback={this.toggleLoadingIndicator}
              passwordValidationError={passwordValidationError}
              inputStyleOverrides={styles.inputFields}
              nextUrlParam={this.getNextUrlParam()}
              loginButtonTextOverride={i18n._('Continue')}
              hideEmailField
              hidePasswordField={
                !!this.state.userInfo &&
                !this.state.userInfo.has_password &&
                // Show all login methods if we don't know the user's signup method
                !this.isUnidentifiedSignupMethod()
              }
              hideFacebookButton={
                !!this.state.userInfo &&
                !this.state.userInfo.connected_to_facebook &&
                // Show all login methods if we don't know the user's signup method
                !this.isUnidentifiedSignupMethod()
              }
              hideGoogleButton={
                !!this.state.userInfo &&
                !this.state.userInfo.connected_to_google &&
                // Show all login methods if we don't know the user's signup method
                !this.isUnidentifiedSignupMethod()
              }
              hidePasswordReset
              hideTermsOfService
              onEmailChange={function() {}}
              onSocialConnectVerified={this.showAdditionalStep(SignupTypes.socialSignupWithAge)}
              onFBPhoneOnlyConnectVerified={this.showAdditionalStep(SignupTypes.FBEmailCollection)}
              onLoginWithMfa={this.handleLoginWithMfa.bind(this)}
              onPasswordChange={this.handleDataChange.bind(this, 'password')}
              isPersonalizedModal={isPersonalizedModal}
              password={this.state.password}
              shouldPreventRegister
              triggerAccountRecoveryMode={this.handlePreventRegisterFromPersonalizedModal}
              showLoginWelcomeLoadingFn={this.showLoginWelcomeLoadingFn}
              showResponseMessageModal={
                type === SignupTypes.respondMessage
                  ? this.props.showResponseMessageModal
                  : undefined
              }
            />
          )}
        </RequestContextConsumer>
        <Box paddingY={5}>
          <Divider />
        </Box>

        <Box data-test-id="login-switch-account">
          <Touchable onTouch={this.handleNotYouClick} fullWidth={false}>
            <Text align="center" size="sm" weight="bold">
              <Link href={'#'}>{notYouText}</Link>
            </Text>
          </Touchable>
        </Box>

        <Box marginTop={1} marginBottom={4}>
          <Touchable
            onTouch={
              isBusiness
                ? () => {
                    track(PCONST.thrift.EventType.CLICK, {
                      element: PCONST.thrift.ElementType.NO_ACCOUNT_LINK,
                    });
                  }
                : this.handleSignupToggleFromPersonalized
            }
            fullWidth={false}
          >
            <Text align="center" size="sm" weight="bold">
              <Link href={isBusiness ? this.getNextUrl('/business/create') : '#'}>
                {needAccountText}
              </Link>
            </Text>
          </Touchable>
        </Box>
      </Fragment>
    );

    return sideImage ? (
      <div
        data-test-id="login"
        style={{
          ...styles.personalizedModalContainer,
          display: 'flex',
          width: 'auto',
          padding: '',
        }}
      >
        {this.renderModalSideImage(sideImage)}
        <div style={{ width: 400, padding: '22px 0' }}>{personalizedLoginModal}</div>
      </div>
    ) : (
      <Fragment>
        {/* need to keep personalizedLoginModal mounted while loading to show error message */}
        <div
          data-test-id="login"
          style={showLoginWelcomeLoading ? { display: 'none' } : styles.personalizedModalContainer}
        >
          {personalizedLoginModal}
        </div>
        {showLoginWelcomeLoading && <LoginWelcomeLoading width={400} />}
      </Fragment>
    );
  }

  renderEmailAlreadyExistLogin() {
    const {
      container,
      darkSignupTitle,
      delayedTitleRender,
      hybridTier,
      isBusiness,
      passwordValidationError,
      type,
      seoUnauthExperiments,
    } = this.props;
    const { showLoginWelcomeLoading } = this.state;

    const notYouText = i18n._(
      'Not you? Log in with a different account',
      'Short message (in small fonts) on the bottom of the login modal that prompts a user to log in to a different account',
    );
    const needAccountText = i18n._(
      'Need an account? Sign up now',
      'Short message (in small fonts) on the bottom of the login modal that prompts a users to create an account',
    );
    const name = this.state.userInfo ? this.state.userInfo.first_name : '';
    const welcomeNameText = interpolateNamedTemplate(
      i18n._(
        "Looks like you're already on Pinterest",
        'Short title (large font) on the login modal welcoming back a user who previously logged in to Pinterest',
      ),
      { name },
    );

    const expGroup =
      seoUnauthExperiments.v2GetGroup('web_unauth_desktop_fb_phone_only_sign_up_v3') || '';

    const logoContainerStyles = styles.logoContainer;
    const signupTitleContainer = {
      ...styles.signupTitleContainer,
      width: '350px',
      overflow: 'hidden',
      maxHeight: '120px',
    };

    const emailAleadyExistModal = (
      <Fragment>
        {this.renderLoadingIndicator()}
        <div style={logoContainerStyles}>{logo(40, true)}</div>
        <div style={signupTitleContainer}>
          {!delayedTitleRender && (
            <FullPageSignupTitle
              container={container}
              darkSignupTitle={darkSignupTitle}
              hybridTier={hybridTier}
              modalType={ModalTypes.personalizedLogin}
              titleText={welcomeNameText}
            />
          )}
        </div>
        <Box marginBottom={3}>
          <Text size="md" align="center">
            {this.state.socialRegistrationCredentials.userData
              ? this.state.socialRegistrationCredentials.userData.email
              : null}
          </Text>
        </Box>
        <RequestContextConsumer>
          {(rq) => (
            <Login
              requestContext={rq}
              container={this.props.container}
              disablePostSignupNextUrl
              email={
                this.state.socialRegistrationCredentials.userData
                  ? this.state.socialRegistrationCredentials.userData.email
                  : null
              }
              loadingIndicatorCallback={this.toggleLoadingIndicator}
              passwordValidationError={passwordValidationError}
              inputStyleOverrides={styles.inputFields}
              nextUrlParam={this.getNextUrlParam()}
              loginButtonTextOverride={i18n._('Continue')}
              hideEmailField
              hideFacebookButton
              hideGoogleButton={expGroup && expGroup.startsWith('enabled_email_only')}
              hidePasswordReset
              hideTermsOfService
              onEmailChange={function() {}}
              onSocialConnectVerified={this.showAdditionalStep(SignupTypes.socialSignupWithAge)}
              onFBPhoneOnlyConnectVerified={this.showAdditionalStep(SignupTypes.FBEmailCollection)}
              onLoginWithMfa={this.handleLoginWithMfa.bind(this)}
              onPasswordChange={this.handleDataChange.bind(this, 'password')}
              password={this.state.password}
              shouldPreventRegister
              triggerAccountRecoveryMode={this.handlePreventRegisterFromPersonalizedModal}
              showLoginWelcomeLoadingFn={this.showLoginWelcomeLoadingFn}
              showResponseMessageModal={
                type === SignupTypes.respondMessage
                  ? this.props.showResponseMessageModal
                  : undefined
              }
            />
          )}
        </RequestContextConsumer>
        <Box paddingY={5}>
          <Divider />
        </Box>

        <Box data-test-id="login-switch-account">
          <Touchable onTouch={this.handleNotYouClick} fullWidth={false}>
            <Text align="center" size="sm" weight="bold">
              <Link href={'#'}>{notYouText}</Link>
            </Text>
          </Touchable>
        </Box>

        <Box marginTop={1} marginBottom={4}>
          <Touchable
            onTouch={
              isBusiness
                ? () => {
                    track(PCONST.thrift.EventType.CLICK, {
                      element: PCONST.thrift.ElementType.NO_ACCOUNT_LINK,
                    });
                  }
                : this.handleSignupToggleFromPersonalized
            }
            fullWidth={false}
          >
            <Text align="center" size="sm" weight="bold">
              <Link href={isBusiness ? this.getNextUrl('/business/create') : '#'}>
                {needAccountText}
              </Link>
            </Text>
          </Touchable>
        </Box>
      </Fragment>
    );

    return (
      <Fragment>
        {/* need to keep personalizedLoginModal mounted while loading to show error message */}
        <div
          data-test-id="login"
          style={showLoginWelcomeLoading ? { display: 'none' } : styles.personalizedModalContainer}
        >
          {emailAleadyExistModal}
        </div>
        {showLoginWelcomeLoading && <LoginWelcomeLoading width={400} />}
      </Fragment>
    );
  }

  renderSignupToLoginToggle() {
    return (
      <SignupLoginToggleButton
        buttonText={i18n._(
          'Already a member? Log in',
          'Toggle to switch from the sign up modal to the login modal ',
        )}
        link="/login/"
        onClick={this.handleLoginToggleButtonClick}
        titleStylesOverride={styles.signupLoginToggle}
        useSmallButton
      />
    );
  }
  renderSocialSignupModalText = (validInvite, defaultHeader, signupTitleContainerStyles) => {
    const {
      container,
      darkSignupTitle,
      delayedTitleRender,
      hybridTier,
      inviter,
      type,
    } = this.props;
    if (validInvite) {
      const firstName = inviter ? inviter.first_name : '';
      const image = inviter ? inviter.image_medium_url : '';
      const isResponseSignup = type === SignupTypes.respondMessage;
      const signupText = isResponseSignup
        ? i18n
            ._(
              'Sign up and let {{ name }} know what you think',
              'Prompt user to signup to access all Pinterest features',
            )
            .replace('{{ name }}', firstName)
        : i18n
            ._(
              'Join {{ name }} on Pinterest for more ideas',
              'Prompt user to signup to access all Pinterest features',
            )
            .replace('{{ name }}', firstName);

      return (
        <Fragment>
          <div style={styles.profilePic}>
            <Avatar src={image} name={firstName} verified={isResponseSignup} icon="pinterest" />
          </div>
          <div style={signupTitleContainerStyles}>
            {!delayedTitleRender && (
              <FullPageSignupTitle
                container={container}
                darkSignupTitle={darkSignupTitle}
                hybridTier={hybridTier}
                modalType={ModalTypes.signup}
                titleText={signupText}
              />
            )}
          </div>
        </Fragment>
      );
    }
    return defaultHeader;
  };

  renderDefaultSignup() {
    const {
      container,
      darkSignupTitle,
      delayedTitleRender,
      emailValidationError,
      passwordValidationError,
      signupSource,
      seoUnauthExperiments,
      viewer,
      renderBusinessCreateButton,
      inviter,
      pageType,
      hybridTier,
      requestContext,
      eligibleExperiences,
      type,
      pinCategory,
    } = this.props;
    const {
      age,
      email,
      gender,
      isExitingUserInSignup,
      name,
      password,
      showLoginWelcomeLoading,
    } = this.state;

    let taglineText =
      this.props.customSignupTagline ||
      (signupSource
        ? this.tagline(this.state.type, signupSource, seoUnauthExperiments)
        : this.tagline(this.state.type, SignupSource.defaultSource, seoUnauthExperiments));

    if (isExitingUserInSignup) {
      taglineText = i18n._(
        'Welcome back to Pinterest!',
        'welcoming user back to pinterest in signup modal when we recognize their email',
      );
    }
    const signupStyles = {
      ...this.getSignupStyles(),
      width: '440px',
      ...(container === 'home_page' ? { paddingBottom: 55 } : null),
    };
    const signupFormStyles = styles.signupForm;
    const logoContainerStyles = styles.logoContainer;
    let signupTitleContainerStyles = styles.signupTitleContainer;
    if (container !== 'login_page') {
      signupTitleContainerStyles = { ...signupTitleContainerStyles, width: '355px' };
    }
    const signupSubTitleContainerStyles = styles.signupTitleContainer;
    const signupSubtitlesStyles = styles.signupSubtitles;
    let additionalChildren = (
      <div>
        <Separator css={styles.separator} />
        <div>{this.renderSignupToLoginToggle()}</div>
      </div>
    );

    if (this.props.toggleType === FullPageSignupToggleType.NOT_NOW_BUTTON) {
      additionalChildren = (
        <div>
          <Separator css={styles.separator} />
          <div>{this.renderSignupToLoginToggle()}</div>
          <div>
            <div style={styles.businessContainer}>
              <h5 style={styles.businessHeader}>{i18n._('Are you a business? ')}</h5>
              <a
                href={cannonicalBusinessSignupURL}
                style={styles.businessLink}
                target="_blank"
                onClick={() => {
                  trackRegisterAction('unauth.business_link.click');
                  if (viewer.isAuth) {
                    track(PCONST.thrift.EventType.CLICK, {
                      component: PCONST.thrift.ComponentType.SIGNUP_FORM,
                      element: PCONST.thrift.ElementType.GET_STARTED_BUSINESS_LINK,
                      login_state: viewer.isLimitedLogin
                        ? PCONST.LoginStates.LIMITED
                        : PCONST.LoginStates.DEFAULT,
                    });
                  } else {
                    track(PCONST.thrift.EventType.CLICK, {
                      component: PCONST.thrift.ComponentType.SIGNUP_FORM,
                      element: PCONST.thrift.ElementType.GET_STARTED_BUSINESS_LINK,
                    });
                  }
                }}
              >
                {i18n._('Get started here')}
              </a>
            </div>
          </div>
        </div>
      );
    } else if (container === 'home_page') {
      additionalChildren = <Box marginTop={3}>{this.renderSignupToLoginToggle()}</Box>;
    }
    let logoSize = 40;
    if (signupSource === SignupSource.homePage) {
      logoSize = 50;
    }
    const valuePropAsTagline = this.valuePropAsTitle();
    const validInvite = inviter ? !inviter.is_default_image : false;
    const signupTitleExpName = pinCategory ? signupTitleExpNameCategoryMap[pinCategory] : null;
    const signupTitleExpGroup = signupTitleExpName
      ? seoUnauthExperiments.v2ActivateExperiment(signupTitleExpName) || ''
      : '';
    const defaultHeader = (
      <Fragment>
        <div style={logoContainerStyles}>{logo(logoSize, true)}</div>
        <div style={signupTitleContainerStyles}>
          {!delayedTitleRender &&
            (signupTitleExpGroup ? (
              <FullPageSignupTitle
                container={container}
                hybridTier={hybridTier}
                darkSignupTitle={darkSignupTitle}
                modalType={ModalTypes.signup}
                titleText={
                  !['login_page', 'home_page'].includes(container)
                    ? this.signupTitleTextExpGroupMapping(signupTitleExpGroup)
                    : taglineText
                }
              />
            ) : (
              <FullPageSignupTitle
                container={container}
                hybridTier={hybridTier}
                darkSignupTitle={darkSignupTitle}
                modalType={ModalTypes.signup}
                titleText={
                  !['login_page', 'home_page'].includes(container)
                    ? valuePropAsTagline
                    : taglineText
                }
              />
            ))}
        </div>
        <div style={signupSubTitleContainerStyles}>
          {!this.state.isExitingUserInSignup && (
            <h3 style={signupSubtitlesStyles}>
              {container !== 'login_page' && container !== 'home_page'
                ? taglineText
                : this.valuePropAsSubtitle(signupSource)}
            </h3>
          )}
        </div>
      </Fragment>
    );

    const isInEducationVideoInModalGroup =
      EducationVideoExperimentUtils(
        seoUnauthExperiments,
        eligibleExperiences,
        pageType,
        true,
        hybridTier,
        requestContext.isBot,
      ) &&
      (this.state.type === SignupTypes.login || this.state.type === SignupTypes.signup);

    const signupModal = (
      <Fragment>
        {this.renderLoadingIndicator()}
        <div style={validInvite ? styles.signupInviteForm : signupFormStyles}>
          {this.renderSocialSignupModalText(validInvite, defaultHeader, signupTitleContainerStyles)}
          <Signup
            additionalEmailPasswordChildren={additionalChildren}
            age={age}
            container={this.props.container}
            dark
            disablePostSignupNextUrl
            email={email}
            emailValidationError={emailValidationError}
            loadingIndicatorCallback={this.toggleLoadingIndicator}
            passwordValidationError={passwordValidationError}
            gender={gender}
            setWelcomeBackMessageForExistingEmail={this.setWelcomeBackMessageForExistingEmail}
            name={name}
            nextUrlParam={this.props.postSignupUrlParam || this.getNextUrlParam()}
            onAgeChange={this.handleDataChange.bind(this, 'age')}
            onEmailChange={this.handleDataChange.bind(this, 'email')}
            onFBPhoneOnlyConnectVerified={this.showAdditionalStep(SignupTypes.FBEmailCollection)}
            onFBEmailDeniedPerm={this.showAdditionalStep(SignupTypes.FBEmailRerequest)}
            onSocialConnectVerified={this.showAdditionalStep(SignupTypes.socialSignupWithAge)}
            onGenderChange={this.handleDataChange.bind(this, 'gender')}
            onLoginWithMfa={this.handleLoginWithMfa.bind(this)}
            onNameChange={this.handleDataChange.bind(this, 'name')}
            onPasswordChange={this.handleDataChange.bind(this, 'password')}
            onStepChange={this.changeStep.bind(this)}
            onShowRegisterForm={this.handleShowRegisterForm}
            setNameFromEmail={this.setNameFromEmail.bind(this)}
            signupSource={signupSource}
            showSignupWelcomeLoadingFn={this.showSignupWelcomeLoadingFn}
            password={password}
            clearPassword={this.clearPassword}
            showLoginWelcomeLoadingFn={this.showLoginWelcomeLoadingFn}
            showResponseMessageModal={
              type === SignupTypes.respondMessage ? this.props.showResponseMessageModal : undefined
            }
          />
        </div>
        {renderBusinessCreateButton && renderBusinessCreateButton()}
      </Fragment>
    );

    const expGroup = seoUnauthExperiments.v2GetGroup('web_unauth_plp_education_video') || '';
    // Added a false value for now to disable the in modal version
    // eslint-disable-next-line no-constant-condition
    return false && expGroup.startsWith('enabled') && isInEducationVideoInModalGroup ? (
      <Experience
        placementId={PCONST.thrift.Placement.UNAUTH_WEB_PIN_PAGE_TAKEOVER}
        experienceIds={[PCONST.thrift.Experience.WEB_UNAUTH_PLP_EDUCATION_VIDEO]}
      >
        {({ complete, display_data }) => {
          if (
            !display_data[expGroup] ||
            !display_data[expGroup].url ||
            !display_data[expGroup].thumbnail ||
            !display_data[expGroup].duration
          ) {
            return null;
          }
          return (
            <div data-test-id="signup" style={{ ...signupStyles, display: 'flex', width: 'auto' }}>
              {this.renderModalSideVideo(
                true,
                display_data[expGroup].url,
                display_data[expGroup].thumbnail,
                display_data[expGroup].duration,
              )}
              <div style={{ width: 440 }}>{signupModal}</div>
            </div>
          );
        }}
      </Experience>
    ) : (
      <Fragment>
        {/* need to keep signupModal mounted while loading to show error message */}
        <div
          data-test-id="signup"
          style={showLoginWelcomeLoading ? { display: 'none' } : signupStyles}
        >
          {signupModal}
        </div>
        {showLoginWelcomeLoading && <LoginWelcomeLoading width={440} />}
      </Fragment>
    );
  }
  renderLoginToSignupToggle(link) {
    return (
      <SignupLoginToggleButton
        buttonText={i18n._(
          'Not on Pinterest yet? Sign up',
          'Toggle to switch from the login modal to the sign up modal',
        )}
        link={link}
        onClick={this.handleSignupToggleButtonClick}
        titleStylesOverride={styles.signupLoginToggle}
        useSmallButton
      />
    );
  }
  renderDefaultLogin() {
    const {
      container,
      customLoginTagline,
      darkSignupTitle,
      delayedTitleRender,
      emailValidationError,
      isAccountSwitch,
      hideAccountSwitchValueProp,
      magicLinkGroup,
      passwordValidationError,
      seoUnauthExperiments,
      signupSource,
      renderBusinessCreateButton,
      pageType,
      hybridTier,
      requestContext,
      eligibleExperiences,
      type,
    } = this.props;
    const { showLoginWelcomeLoading } = this.state;

    const taglineText =
      customLoginTagline ||
      (signupSource
        ? this.tagline(this.state.type, signupSource, seoUnauthExperiments)
        : this.tagline(this.state.type, SignupSource.login, seoUnauthExperiments));
    const logoContainerStyles = styles.logoContainer;
    let signupTitleContainerStyles = styles.signupTitleContainer;
    if (container !== 'login_page') {
      signupTitleContainerStyles = { ...signupTitleContainerStyles, width: '355px' };
    }
    let logoSize = 40;
    if (signupSource === SignupSource.homePage) {
      logoSize = 50;
    }
    const showBusinessCreateButtonInLoginModal = false;
    const signupStyles = {
      ...this.getSignupStyles(),
      width: '440px',
      ...(showBusinessCreateButtonInLoginModal && container === 'home_page'
        ? { paddingBottom: 55 }
        : null),
    };
    const signupFormStyles = styles.signupForm;
    const signupSubTitleContainerStyles = styles.signupTitleContainer;
    const signupSubtitlesStyles = styles.signupSubtitles;
    const separatorStyles = { ...styles.separator, marginLeft: '-10px' };

    let toggleButton = (
      <div>
        <Separator css={separatorStyles} />
        <div>{this.renderLoginToSignupToggle('/signup/')}</div>
      </div>
    );

    if (this.props.isBusiness) {
      toggleButton = (
        <div>
          <Separator css={{ margin: '10px auto' }} />
          <a href={this.getNextUrl('/business/create')}>
            {i18n._(
              'Create new account',
              'Link to the page to create a new business account instead of logging in',
            )}
          </a>
          <div />
        </div>
      );
    } else if (this.props.toggleType === FullPageSignupToggleType.NOT_NOW_BUTTON) {
      toggleButton = (
        <div>
          <Separator css={{ margin: '10px auto' }} />
          <div style={styles.haveAccountText}>{this.renderLoginToSignupToggle()}</div>
          <div />
        </div>
      );
    } else if (this.props.toggleType === FullPageSignupToggleType.NO_TOGGLE || isAccountSwitch) {
      toggleButton = null;
    } else if (this.props.toggleType === FullPageSignupToggleType.SMALL_TOGGLE) {
      toggleButton = (
        <div>
          <Separator css={{ margin: '10px auto' }} />
          {this.renderLoginToSignupToggle()}
          <div />
        </div>
      );
    }
    const accountSwitchValueProp = i18n._(
      'Have another Pinterest account? Log in to easily switch between them',
      'Subtitle on the login modal that explains to a user why they would add an additional account to their profile',
    );

    const isInEducationVideoInModalGroup =
      EducationVideoExperimentUtils(
        seoUnauthExperiments,
        eligibleExperiences,
        pageType,
        true,
        hybridTier,
        requestContext.isBot,
      ) &&
      (this.state.type === SignupTypes.login || this.state.type === SignupTypes.signup);

    const loginModal = (
      <Fragment>
        {this.renderLoadingIndicator()}
        <div style={signupFormStyles}>
          <div style={logoContainerStyles}>{logo(logoSize, true)}</div>
          <div style={signupTitleContainerStyles}>
            {!delayedTitleRender && (
              <FullPageSignupTitle
                container={container}
                hybridTier={hybridTier}
                darkSignupTitle={darkSignupTitle}
                modalType={ModalTypes.login}
                titleText={taglineText}
              />
            )}
          </div>
          {isAccountSwitch &&
            !hideAccountSwitchValueProp && (
              <div style={signupSubTitleContainerStyles}>
                <h3 style={signupSubtitlesStyles}>{accountSwitchValueProp}</h3>
              </div>
            )}
          <RequestContextConsumer>
            {(rq) => (
              <Login
                requestContext={rq}
                container={this.props.container}
                disablePostSignupNextUrl
                email={this.state.email}
                emailValidationError={emailValidationError}
                loadingIndicatorCallback={this.toggleLoadingIndicator}
                passwordValidationError={passwordValidationError}
                inputStyleOverrides={styles.inputFields}
                isAccountSwitch={isAccountSwitch}
                magicLinkGroup={magicLinkGroup}
                nextUrlParam={this.getNextUrlParam()}
                onEmailChange={this.handleDataChange.bind(this, 'email')}
                onSocialConnectVerified={this.showAdditionalStep(SignupTypes.socialSignupWithAge)}
                onFBPhoneOnlyConnectVerified={this.showAdditionalStep(
                  SignupTypes.FBEmailCollection,
                )}
                onLoginWithMfa={this.handleLoginWithMfa.bind(this)}
                onPasswordChange={this.handleDataChange.bind(this, 'password')}
                password={this.state.password}
                toggleMagicLinkConfirmation={this.toggleMagicLinkConfirmation}
                clearPassword={this.clearPassword}
                showLoginWelcomeLoadingFn={this.showLoginWelcomeLoadingFn}
                showResponseMessageModal={
                  type === SignupTypes.respondMessage
                    ? this.props.showResponseMessageModal
                    : undefined
                }
              />
            )}
          </RequestContextConsumer>
          {toggleButton}
        </div>
        {showBusinessCreateButtonInLoginModal && renderBusinessCreateButton()}
      </Fragment>
    );

    const expGroup = seoUnauthExperiments.v2GetGroup('web_unauth_plp_education_video') || '';
    // Added a false value for now to disable the in modal version
    return false && expGroup.startsWith('enabled') && isInEducationVideoInModalGroup ? (
      <div data-test-id="login" style={{ ...signupStyles, display: 'flex', width: 'auto' }}>
        <Experience
          placementId={PCONST.thrift.Placement.UNAUTH_WEB_PIN_PAGE_TAKEOVER}
          experienceIds={[PCONST.thrift.Experience.WEB_UNAUTH_PLP_EDUCATION_VIDEO]}
        >
          {({ complete, display_data }) => {
            if (
              !display_data[expGroup] ||
              !display_data[expGroup].url ||
              !display_data[expGroup].thumbnail ||
              !display_data[expGroup].duration
            ) {
              complete();
              return null;
            }
            return this.renderModalSideVideo(
              false,
              display_data[expGroup].url,
              display_data[expGroup].thumbnail,
              display_data[expGroup].duration,
            );
          }}
        </Experience>
        <div style={{ width: 440 }}>{loginModal}</div>
      </div>
    ) : (
      <Fragment>
        {/* need to keep loginModal mounted while loading to show error message */}
        <div
          data-test-id="login"
          style={showLoginWelcomeLoading ? { display: 'none' } : signupStyles}
        >
          {loginModal}
        </div>
        {showLoginWelcomeLoading && <LoginWelcomeLoading width={440} />}
      </Fragment>
    );
  }
  renderLoginMfa() {
    const {
      container,
      darkSignupTitle,
      delayedTitleRender,
      hybridTier,
      seoUnauthExperiments,
    } = this.props;

    const taglineText = this.tagline(this.state.type, SignupSource.loginMfa, seoUnauthExperiments);
    const signupStyles = this.getSignupStyles();
    const signupFormStyles = styles.signupForm;
    const logoContainerStyles = styles.logoContainer;

    return (
      <div data-test-id="login" style={signupStyles}>
        <div style={signupFormStyles}>
          <div style={logoContainerStyles}>{logo(40, true)}</div>
          <div style={{ ...styles.signupTitleContainer, ...styles.signupTitleMfaContainer }}>
            {!delayedTitleRender && (
              <FullPageSignupTitle
                container={container}
                darkSignupTitle={darkSignupTitle}
                hybridTier={hybridTier}
                modalType={ModalTypes.loginMfa}
                titleText={taglineText}
              />
            )}
          </div>
          <LoginMfa
            container={this.props.container}
            code={this.state.mfaCode}
            creds={this.state.mfaCreds}
            email={this.state.email}
            inputStyleOverrides={styles.inputFields}
            isAccountSwitch={this.props.isAccountSwitch}
            nextUrlParam={this.getNextUrlParam()}
            onCodeChange={this.handleDataChange.bind(this, 'mfaCode')}
            password={this.state.password}
            phoneNumberEnd={this.state.mfaPhoneNumberEnd}
          />
        </div>
      </div>
    );
  }
  renderMagicLinkConfirmation() {
    const { email, password } = this.state;
    trackMagicLink('confirmation_shown', email, password);
    return (
      <div data-test-id="login" style={this.getSignupStyles()}>
        <div style={styles.signupForm}>
          <div style={styles.logoContainer}>{logo(40, true)}</div>
          <div style={styles.signupTitleContainer}>
            <h3 style={styles.signupTitle}>
              {i18n._('Check your email', 'title on login modal telling user to check their email')}
            </h3>
          </div>
          <Box direction="column" alignItems="center" display="flex">
            <Box paddingY={3} width={270}>
              <Text size="lg">
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: StringUtil.dangerouslyFormat(
                      i18n._(
                        'We just sent an email to you at <strong>{{ email }}</strong>. It has a magic link that you can use to log in.',
                        'Message on login modal telling users an email has been sent to them containing a link to log in',
                      ),
                      {
                        email,
                      },
                    ),
                  }}
                />
              </Text>
            </Box>
            <Box paddingY={3} width={270}>
              <GestaltButton
                text={i18n._(
                  'Back',
                  'text on button to return to login modal from magic link confirmation screen',
                )}
                onClick={() => {
                  trackMagicLink('confirmation_dismissed', email, password);
                  this.toggleMagicLinkConfirmation(false);
                }}
              />
            </Box>
          </Box>
        </div>
      </div>
    );
  }
  renderSocialSignupWithAge() {
    const { socialRegistrationCredentials } = this.state;
    const { type } = this.props;

    const taglineText = i18n._(
      'Sign up to see more',
      'Age collection signup step for Facebook/G+ authentication',
    );

    return (
      <div data-test-id="login" style={this.getSignupBoxStyle()}>
        {this.renderLoadingIndicator()}
        <div style={styles.signupForm}>
          <div style={styles.logoContainer}>{logo(40, true)}</div>
          <div style={styles.signupTitleContainer}>
            <h3 style={styles.signupTitle}>{taglineText}</h3>
          </div>
          <SocialConnectAgeCollection
            container={this.props.container}
            disablePostSignupNextUrl
            loadingIndicatorCallback={this.toggleLoadingIndicator}
            nextUrlParam={this.getNextUrlParam()}
            registrationCredentials={socialRegistrationCredentials}
            showSignupWelcomeLoadingFn={this.showSignupWelcomeLoadingFn}
            showResponseMessageModal={
              type === SignupTypes.respondMessage ? this.props.showResponseMessageModal : undefined
            }
          />
        </div>
        <Box margin={4}>
          <Divider />
          <Box padding={3}>{this.renderSignupToLoginToggle()}</Box>
        </Box>
      </div>
    );
  }

  renderFBEmailCollection() {
    const { socialRegistrationCredentials } = this.state;
    const { nextUrlParam, seoUnauthExperiments } = this.props;
    const formContainerStyle = { ...styles.signup, minHeight: '400px' };
    const titleContainerStyle = { ...styles.signupTitleContainer, width: '310px' };
    const expGroup =
      seoUnauthExperiments.v2GetGroup('web_unauth_desktop_fb_phone_only_sign_up_v3') || '';

    // TODO: use this string to replace the one on line 1978 once the translation is done
    i18n._("What's your email address?", 'email collection step for Facebook phone only sign up');
    const taglineText =
      expGroup.indexOf('copy_variant') === -1
        ? i18n._('Sign up with Facebook', 'email collection step for Facebook phone only sign up')
        : i18n._(
            "What's your email address?",
            'Prompt for email address in after sign in with apple',
          );
    return (
      <div data-test-id="login" style={formContainerStyle}>
        {this.renderLoadingIndicator()}
        <div style={styles.signupForm}>
          <div style={styles.logoContainer}>{logo(40, true)}</div>
          <div style={titleContainerStyle}>
            <h3 style={styles.signupTitle}>{taglineText}</h3>
          </div>
          <FacebookEmailCollection
            nextUrlParam={nextUrlParam}
            loadingIndicatorCallback={this.toggleLoadingIndicator}
            showEmailConfirmationStep={this.showAdditionalStep(SignupTypes.emailConfirmation)}
            showEmailAlreadyExistModal={this.showAdditionalStep(SignupTypes.FBEmailAlreadyExist)}
            showLoginStep={this.showAdditionalStep(SignupTypes.login)}
            showSocialSignupWithAgeStep={this.showAdditionalStep(SignupTypes.socialSignupWithAge)}
            registrationCredentials={socialRegistrationCredentials}
            seoUnauthExperiments={seoUnauthExperiments}
            onNotNowClick={this.handleNotNowClick}
          />
        </div>
      </div>
    );
  }

  renderFBEmailRerequestForm() {
    const { type } = this.props;
    const formContainerStyle = { ...this.getSignupBoxStyle(), minHeight: '400px' };
    const titleContainerStyle = { ...styles.signupTitleContainer, width: '320px' };
    const taglineText = i18n._('Add your email to finish', 'Tagline on facebook rerequest form');
    const subtitleStyles = { ...styles.signupSubtitles, marginTop: '24px' };
    const fbButtonStyle = { width: '260px', margin: 'auto' };
    const notNowButtonStyle = { ...styles.notNowButton, marginTop: '90px' };
    const message = i18n._(
      'We need your email address to complete this signup. Click the button below, and we can use the email associated with your Facebook account.',
      'message to explain reauth step beneath the tagline',
    );
    return (
      <div data-test-id="login" style={formContainerStyle}>
        {this.renderLoadingIndicator()}
        <div style={styles.signupForm}>
          <div style={styles.logoContainer}>{logo(40, true)}</div>
          <div style={titleContainerStyle}>
            <h3 style={styles.signupTitle}>{taglineText}</h3>
            <h3 style={subtitleStyles}>{message}</h3>
            <div style={fbButtonStyle}>
              <FacebookConnectButton
                isRerequest
                container="FBEmailRerequest"
                buttonText={i18n._(
                  'Continue with Facebook',
                  'Button text on facebook connect button on FB email reauth form',
                )}
                loadingIndicatorCallback={this.toggleLoadingIndicator}
                useGestaltTooltip
                handlePostConnectClick={this.handlePostConnectClick}
                handlePostSuccessConnect={this.handlePostSuccessConnect}
                showResponseMessageModal={
                  type === SignupTypes.respondMessage
                    ? this.props.showResponseMessageModal
                    : undefined
                }
              />
            </div>
          </div>
          <Button
            className="lightGrey"
            onClick={this.handleNotNowClick}
            styleOverrides={notNowButtonStyle}
          >
            {i18n._('Not now', 'word on not now button to dismiss the FB email reauth form')}
          </Button>
        </div>
      </div>
    );
  }
  renderEmailConfirmation() {
    const { socialRegistrationCredentials } = this.state;
    const email = socialRegistrationCredentials.userData.email;
    const taglineText = i18n._(
      'Check your email',
      'email confirmation tagline for Facebook cellphone only sign up',
    );
    const confirmationMessage = i18n._(
      "We've sent a confirmation email to ",
      'email confirmation message for Facebook cellphone only sign up',
    );
    const subtitleStyles = { ...styles.signupSubtitles, marginTop: '24px' };
    return (
      <div data-test-id="login" style={this.getSignupBoxStyle()}>
        <div style={styles.signupForm}>
          <div style={styles.logoContainer}>{logo(40, true)}</div>
          <div style={styles.signupTitleContainer}>
            <h3 style={styles.signupTitle}>{taglineText}</h3>
            <h3 style={subtitleStyles}>
              {confirmationMessage}
              {email}
            </h3>
          </div>
          <Button
            className="red"
            onClick={this.handleConfirmationPageButtonClick}
            styleOverrides={styles.onClickEmailConfirmationButton}
          >
            {i18n._('Done', 'word on the button in email confirmation page')}
          </Button>
        </div>
      </div>
    );
  }
  render() {
    const { isAccountSwitch, isPersonalizedModal, showSignupWelcomeLoading, type } = this.state;
    const { viewer } = this.props;

    if (type === SignupTypes.signupOrPersonalizedLogin) {
      if (isPersonalizedModal && !isAccountSwitch) {
        return this.renderPersonalizedLogin();
      }
      return this.renderDefaultSignup();
    } else if (type === SignupTypes.signup) {
      if (showSignupWelcomeLoading) {
        return <SignupWelcomeLoading />;
      }
      return this.renderDefaultSignup();
    } else if (type === SignupTypes.login) {
      if (showSignupWelcomeLoading) {
        return <SignupWelcomeLoading />;
      }
      if (isPersonalizedModal && !isAccountSwitch && (!viewer.isAuth || viewer.isLimitedLogin)) {
        return this.renderPersonalizedLogin();
      }
      return this.renderDefaultLogin();
    } else if (type === SignupTypes.loginMfa) {
      return this.renderLoginMfa();
    } else if (type === SignupTypes.socialSignupWithAge) {
      if (showSignupWelcomeLoading) {
        return <SignupWelcomeLoading />;
      }
      return this.renderSocialSignupWithAge();
    } else if (type === SignupTypes.FBEmailRerequest) {
      return this.renderFBEmailRerequestForm();
    } else if (type === SignupTypes.emailConfirmation) {
      return this.renderEmailConfirmation();
    } else if (type === SignupTypes.magicLinkConfirmation) {
      return this.renderMagicLinkConfirmation();
    } else if (type === SignupTypes.respondMessage) {
      return this.renderDefaultSignup();
    } else if (this.state.type === SignupTypes.FBEmailCollection) {
      return this.renderFBEmailCollection();
    } else if (this.state.type === SignupTypes.FBEmailAlreadyExist) {
      return this.renderEmailAlreadyExistLogin();
    }
    return null;
  }
}
const mapStateToProps = (state: { ...ReduxState, pins?: { [key: string]: CloseupPin } }) => ({
  location: state.location,
  pins: state.pins || {},
  userInfo: state.loggedOutUserInfo ? state.loggedOutUserInfo.userInfo : {},
  viewer: state.viewer,
  eligibleExperiences: state.experiences ? state.experiences.eligibleExperiences : {},
  emailTaken: !!state.loggedOutUserInfo && state.loggedOutUserInfo.emailTaken,
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  setEmailAvailable: () => dispatch(setEmailTaken(false)),
});

FullPageSignup.contextTypes = {
  isBot: PropTypes.bool,
  logging: PropTypes.shape({
    viewParameter: PropTypes.number,
    viewType: PropTypes.number.isRequired,
  }),
  locale: PropTypes.string,
};

export default compose(
  withRequestContext,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withSeoExperiments,
  withUnauthSeoExperiments,
)(FullPageSignup);
